import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Alert,
  Typography,
  Box,
  LinearProgress,
  Chip,
  alpha,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { blacklistService } from '../../services/blacklistService';

interface AddBlacklistDialogProps {
  open: boolean;
  onClose: () => void;
}

interface BulkAddResult {
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}

export const AddBlacklistDialog: React.FC<AddBlacklistDialogProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<'ip' | 'prefix'>('ip');
  const [entries, setEntries] = useState('');
  const [reason, setReason] = useState('manual');
  const [notes, setNotes] = useState('');
  const [autoRemoveDays, setAutoRemoveDays] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<BulkAddResult | null>(null);

  const parsedEntries = entries
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));
  const entryCount = parsedEntries.length;

  const handleClose = () => {
    setEntries('');
    setReason('manual');
    setNotes('');
    setAutoRemoveDays('');
    setError(null);
    setResult(null);
    setProgress(0);
    setType('ip');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setIsProcessing(true);
    setProgress(0);

    if (parsedEntries.length === 0) {
      setError('No valid entries found');
      setIsProcessing(false);
      return;
    }

    const autoRemove = autoRemoveDays ? parseInt(autoRemoveDays, 10) : undefined;
    const results: BulkAddResult = { total: parsedEntries.length, successful: 0, failed: 0, errors: [] };

    const batchSize = 10;
    for (let i = 0; i < parsedEntries.length; i += batchSize) {
      const batch = parsedEntries.slice(i, i + batchSize);

      await Promise.allSettled(
        batch.map(async (entry) => {
          try {
            if (type === 'ip') {
              await blacklistService.addIp({ ipAddress: entry, reason, notes: notes || undefined, autoRemoveAfterDays: autoRemove });
            } else {
              await blacklistService.addPrefix({ prefix: entry, reason, notes: notes || undefined, autoRemoveAfterDays: autoRemove });
            }
            results.successful++;
          } catch (err: any) {
            results.failed++;
            const errorMsg = err.response?.data?.message || err.response?.data || err.message || 'Unknown error';
            results.errors.push(`${entry}: ${errorMsg}`);
          }
        })
      );

      setProgress(Math.round(((i + batch.length) / parsedEntries.length) * 100));
    }

    setResult(results);
    setIsProcessing(false);
    queryClient.invalidateQueries({ queryKey: ['blacklist'] });

    if (results.failed === 0) {
      setTimeout(handleClose, 1200);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          Add to Blacklist
          {entryCount > 0 && (
            <Chip label={`${entryCount} ${entryCount === 1 ? 'entry' : 'entries'}`} size="small" color="primary" sx={{ fontWeight: 600 }} />
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

            {result && (
              <Alert severity={result.failed === 0 ? 'success' : 'warning'} sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {result.failed === 0 ? 'All entries added successfully!' : 'Completed with errors'}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2">Total: {result.total}</Typography>
                  <Typography variant="body2" color="success.main">OK: {result.successful}</Typography>
                  {result.failed > 0 && <Typography variant="body2" color="error.main">Failed: {result.failed}</Typography>}
                </Stack>
                {result.errors.length > 0 && (
                  <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: alpha('#000', 0.04), borderRadius: 1, maxHeight: 120, overflowY: 'auto' }}>
                    {result.errors.map((err, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem' }}>
                        {err}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {isProcessing && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8125rem' }}>Processing... {progress}%</Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 2 }} />
              </Box>
            )}

            <FormControl>
              <FormLabel sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>Type</FormLabel>
              <RadioGroup row value={type} onChange={(e) => setType(e.target.value as 'ip' | 'prefix')}>
                <FormControlLabel value="ip" control={<Radio size="small" disabled={isProcessing} />} label="IP Addresses" />
                <FormControlLabel value="prefix" control={<Radio size="small" disabled={isProcessing} />} label="CIDR Prefixes" />
              </RadioGroup>
            </FormControl>

            <TextField
              label={type === 'ip' ? 'IP Addresses (one per line)' : 'Prefixes (one per line)'}
              placeholder={type === 'ip' ? '192.168.1.100\n10.0.0.5\n# Comments ignored' : '192.168.1.0/24\n10.0.0.0/16\n# Comments ignored'}
              value={entries}
              onChange={(e) => setEntries(e.target.value)}
              fullWidth
              required
              multiline
              rows={6}
              disabled={isProcessing}
              sx={{ '& .MuiInputBase-input': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' } }}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                size="small"
                disabled={isProcessing}
              />
              <TextField
                label="Auto-remove (days)"
                type="number"
                value={autoRemoveDays}
                onChange={(e) => setAutoRemoveDays(e.target.value)}
                size="small"
                disabled={isProcessing}
                sx={{ width: 160 }}
                inputProps={{ min: 0 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleClose} disabled={isProcessing} sx={{ borderRadius: 2, textTransform: 'none' }}>
            {result && result.failed === 0 ? 'Close' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isProcessing || entryCount === 0}
            sx={{ borderRadius: 2, textTransform: 'none', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            {isProcessing ? 'Processing...' : `Add ${entryCount} ${entryCount === 1 ? 'Entry' : 'Entries'}`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
